import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../redux/axios";
import { getItemWithExpiry } from "../../lib/utility";

const initialState = {
  subscriptionData: { data: [], currentPage: 0, totalPages: 0, totalItems: 0 },
  isFetchingSubscription: false,
  isSuccessSubscription: false,
  isErrorSubscription: false,
  errorMessageSubscription: "",
  subscriptionLoginData: {},
  isFetchingSubscriptionLogin: false,
  isSuccessSubscriptionLogin: false,
  isErrorSubscriptionLogin: false,
  errorMessageSubscriptionLogin: "",
  numberSubmissionData: {},
  isFetchingNumberSubmission: false,
  isSuccessNumberSubmission: false,
  isErrorNumberSubmission: false,
  errorMessageNumberSubmission: "",
  logoutData: {},
  isFetchingLogout: false,
  isSuccessLogout: false,
  isErrorLogout: false,
  errorMessageLogout: "",
  createSubscription: {},
  isSuccessCreateSubscription: false,
  isErrorCreateSubscription: false,
  errorMessageCreateSubscription: "",
  deleteSubscription: {},
  isSuccessDeleteSubscription: false,
  isErrorDeleteSubscription: false,
  errorMessageDeleteSubscription: "",
  editSubscription: {},
  isSuccessEditSubscription: false,
  isErrorEditSubscription: false,
  errorMessageEditSubscription: "",
  subscriberData: {},
  isFetchingSubscriber: false,
  isSuccessSubscriber: false,
  isErrorSubscriber: false,
  errorMessageSubscriber: "",
};

export const loginSubscription = createAsyncThunk(
  "subscription/login",
  async (loginData, thunkAPI) => {
    try {
      const { username, password } = loginData;
      const { data, status } = await axios.post(
        `/subscription/login?username=${encodeURIComponent(
          username
        )}&password=${encodeURIComponent(password)}`,
        {},
        {
          headers: {
            accept: "*/*",
          },
        }
      );
      if (status.toString().startsWith("2")) {
        return { data, status };
      } else { return thunkAPI.rejectWithValue({ ...data, status }); }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue({ ...err.response.data, status: err.response.status });
    }
  }
);

export const submitNumber = createAsyncThunk(
  "subscription/submitNumber",
  async (numberData, thunkAPI) => {
    try {
      const { number, subscriptionId } = numberData;
      const { data, status } = await axios.post(
        `/subscription/Number?number=${encodeURIComponent(
          number
        )}&subscriptionId=${subscriptionId}`,
        {},
        {
          headers: {
            accept: "*/*",
          },
        }
      );
      if (status.toString().startsWith("2")) {
        return { data, status };
      } else { return thunkAPI.rejectWithValue({ ...data, status }); }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue({ ...err.response.data, status: err.response.status });
    }
  }
);

export const logoutSubscription = createAsyncThunk(
  "subscription/logout",
  async (logoutData = {}, thunkAPI) => {
    try {
      const { username, sessionId } = logoutData;
      let url = `/subscription/logout?username=${encodeURIComponent(username || "")}`;
      if (sessionId) {
        url += `&sessionId=${encodeURIComponent(sessionId)}`;
      }

      const state = thunkAPI.getState();
      const tokenStr =
        logoutData.token ||
        state?.auth?.user?.accessToken ||
        getItemWithExpiry("profileData")?.accessToken ||
        globalThis?.profileData?.accessToken ||
        "";

      const headers = {
        accept: "*/*",
      };
      if (tokenStr) {
        headers.Authorization = tokenStr.startsWith("Bearer ")
          ? tokenStr
          : `Bearer ${tokenStr}`;
      }

      const { data, status } = await axios.post(
        url,
        {},
        {
          headers,
        }
      );
      if (status.toString().startsWith("2")) {
        return { data, status };
      } else { return thunkAPI.rejectWithValue({ ...data, status }); }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue({ ...err.response.data, status: err.response.status });
    }
  }
);

const isJwt = (t) => {
  if (!t || typeof t !== "string") return false;
  const clean = t.startsWith("Bearer ") ? t.slice(7).trim() : t.trim();
  return clean.split(".").length === 3;
};

const resolveValidJwt = (thunkAPI, ...candidates) => {
  for (const c of candidates) {
    if (isJwt(c)) return c.startsWith("Bearer ") ? c : `Bearer ${c}`;
  }

  try {
    const reduxToken = thunkAPI?.getState()?.auth?.user?.accessToken;
    if (isJwt(reduxToken)) return `Bearer ${reduxToken}`;
  } catch (e) {}

  try {
    const profile = getItemWithExpiry("profileData");
    if (isJwt(profile?.accessToken)) return `Bearer ${profile.accessToken}`;
  } catch (e) {}

  if (isJwt(globalThis?.profileData?.accessToken)) {
    return `Bearer ${globalThis.profileData.accessToken}`;
  }

  try {
    const rawSession = sessionStorage.getItem("subscriberSession");
    if (rawSession) {
      const parsed = JSON.parse(rawSession);
      const subToken =
        parsed?.accessToken ||
        parsed?.token ||
        parsed?.jwt ||
        parsed?.data?.accessToken ||
        parsed?.data?.token;
      if (isJwt(subToken)) return `Bearer ${subToken}`;
    }
  } catch (e) {}

  const storageToken =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("accessToken") ||
    localStorage.getItem("accessToken");
  if (isJwt(storageToken)) return `Bearer ${storageToken}`;

  return "";
};

export const submitSubscriber = createAsyncThunk(
  "subscription/submitSubscriber",
  async ({ subscriptionId, token: passedToken, ...subscriberData }, thunkAPI) => {
    try {
      const authHeader = resolveValidJwt(
        thunkAPI,
        passedToken,
        subscriberData.token
      );

      const params = new URLSearchParams();
      if (subscriberData.firstName) params.append("firstName", subscriberData.firstName);
      if (subscriberData.middleName) params.append("middleName", subscriberData.middleName);
      if (subscriberData.lastName) params.append("lastName", subscriberData.lastName);
      if (subscriberData.number) params.append("number", subscriberData.number);
      if (subscriberData.email) params.append("email", subscriberData.email);
      if (subscriberData.age) params.append("age", subscriberData.age);
      if (subscriberData.countryCode) params.append("countryCode", subscriberData.countryCode);

      const headers = {
        "Content-Type": "application/x-www-form-urlencoded",
        accept: "*/*",
      };

      if (authHeader) {
        headers.Authorization = authHeader;
      }

      const url = `/subscription/subscriber?subscriptionId=${encodeURIComponent(subscriptionId)}`;
      const { data, status } = await axios.post(url, params, { headers });
      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);

export const getSubscriptionData = createAsyncThunk(
  "subscription/listSubscibeEntry",
  async (params = {}, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const {
        pageNumber = 0,
        pageSize = 10,
        sortBy = "username",
        sortDirection = "ASC",
        searchTerm,
        startDate,
        endDate,
        clientId,
      } = params;

      let url = `/subscription/listSubscibeEntry?page=${pageNumber}&size=${pageSize}&sortBy=${sortBy}&sortDirection=${sortDirection.toUpperCase()}`;
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      if (startDate) {
    
        let formattedStartDate = startDate;
        if (startDate.includes(" ")) {
          formattedStartDate = startDate.replace(" ", "T");
        }
        if (!formattedStartDate.includes(".")) {
          formattedStartDate = formattedStartDate.replace(/(:\d{2})$/, ':00.000');
        }
        url += `&startDate=${encodeURIComponent(formattedStartDate)}`;
      }
      if (endDate) {
       
        let formattedEndDate = endDate;
        if (endDate.includes(" ")) {
          formattedEndDate = endDate.replace(" ", "T");
        }
        if (!formattedEndDate.includes(".")) {
          formattedEndDate = formattedEndDate.replace(/(:\d{2})$/, ':59.999');
        }
        url += `&endDate=${encodeURIComponent(formattedEndDate)}`;
      }
      if (clientId) {
        url += `&clientId=${clientId}`;
      }
      if (params.toGmt) {
        url += `&toGmt=${encodeURIComponent(params.toGmt)}`;
      }

      const { data, status } = await axios.get(url, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      if (status.toString().startsWith("2")) {
        return {
          data: data.data || [],
          currentPage: data.pageNumber || 0,
          totalPages: data.totalPages || 0,
          totalItems: data.totalElements || 0,
        };
      } else {
        return thunkAPI.rejectWithValue({ ...data, status });
      }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue({
        ...err.response.data,
        status: err.response.status,
      });
    }
  }
);

export const addSubscriptionData = createAsyncThunk(
  "subscription/save",
  async (data, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      const tokenStr = auth.user.accessToken;
      const url = "/subscription/addSubscriptionEntry";
      const response = await axios.post(url, data, {
        headers: {
          Authorization: `Bearer ${tokenStr}`,
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.status === 200 || response.status === 201) {
        return response.data;
      } else { return thunkAPI.rejectWithValue({ ...response.data, status: response.status }); }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue({ ...err.response.data, status: err.response.status });
    }
  }
);

export const editSubscriptionData = createAsyncThunk(
  "subscription/edit-subscription",
  async (data, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      const tokenStr = auth.user.accessToken;
      const url = "/subscription/updateSubscribeEntry";
      const response = await axios.put(url, data, {
        headers: {
          Authorization: `Bearer ${tokenStr}`,
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.status === 200 || response.status === 201) {
        return response.data;
      } else { return thunkAPI.rejectWithValue({ ...response.data, status: response.status }); }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue({ ...err.response.data, status: err.response.status });
    }
  }
);

export const deleteSubscriptionData = createAsyncThunk(
  "subscription/delete-subscription",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const response = await axios.delete(
        `/subscription/deleteSubscribeEntry/${udata.id}`,
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );
      if (response.status === 200 || response.status === 201) {
        return response.data;
      } else { return thunkAPI.rejectWithValue({ ...response.data, status: response.status }); }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue({ ...err.response.data, status: err.response.status });
    }
  }
);

export const subscriptionSlice = createSlice({
  name: "Subscription",
  initialState,
  reducers: {
    clearStateSubscription: (state) => {
      state.subscriptionData = {
        data: [],
        currentPage: 0,
        totalPages: 0,
        totalItems: 0,
      };
      state.isFetchingSubscription = false;
      state.isSuccessSubscription = false;
      state.isErrorSubscription = false;
      state.errorMessageSubscription = "";
    },
    clearStateCreateSubscription: (state) => {
      state.createSubscription = {};
      state.isSuccessCreateSubscription = false;
      state.isErrorCreateSubscription = false;
      state.errorMessageCreateSubscription = "";
    },
    clearStateDeleteSubscription: (state) => {
      state.deleteSubscription = {};
      state.isSuccessDeleteSubscription = false;
      state.isErrorDeleteSubscription = false;
      state.errorMessageDeleteSubscription = "";
    },
    clearStateEditSubscription: (state) => {
      state.editSubscription = {};
      state.isSuccessEditSubscription = false;
      state.isErrorEditSubscription = false;
      state.errorMessageEditSubscription = "";
    },
    clearStateLogin: (state) => {
      state.subscriptionLoginData = {};
      state.isFetchingSubscriptionLogin = false;
      state.isSuccessSubscriptionLogin = false;
      state.isErrorSubscriptionLogin = false;
      state.errorMessageSubscriptionLogin = "";
     
    },
    clearStateNumberSubmission: (state) => {
      state.numberSubmissionData = {};
      state.isFetchingNumberSubmission = false;
      state.isSuccessNumberSubmission = false;
      state.isErrorNumberSubmission = false;
      state.errorMessageNumberSubmission = "";
    },
    clearStateLogout: (state) => {
      state.logoutData = {};
      state.isFetchingLogout = false;
      state.isSuccessLogout = false;
      state.isErrorLogout = false;
      state.errorMessageLogout = "";
    },
    clearStateSubscriber: (state) => {
      state.subscriberData = {};
      state.isFetchingSubscriber = false;
      state.isSuccessSubscriber = false;
      state.isErrorSubscriber = false;
      state.errorMessageSubscriber = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSubscriptionData.fulfilled, (state, action) => {
        state.subscriptionData = {
          data: action.payload.data || [],
          currentPage: action.payload.currentPage || 0,
          totalPages: action.payload.totalPages || 0,
          totalItems: action.payload.totalItems || 0,
        };
        state.isFetchingSubscription = false;
        state.isSuccessSubscription = true;
        state.isErrorSubscription = false;
      })
      .addCase(getSubscriptionData.pending, (state) => {
        state.isFetchingSubscription = true;
      })
      .addCase(getSubscriptionData.rejected, (state, action) => {
        state.subscriptionData = {
          data: [],
          currentPage: 0,
          totalPages: 0,
          totalItems: 0,
        };
        state.isFetchingSubscription = false;
        state.isSuccessSubscription = false;
        state.isErrorSubscription = true;
        state.errorMessageSubscription = action.payload
          ? action.payload.message
          : "Internal error";
      })
      .addCase(addSubscriptionData.fulfilled, (state, action) => {
        state.createSubscription = action.payload;
        state.isFetchingSubscription = false;
        state.isSuccessCreateSubscription = true;
        state.isErrorCreateSubscription = false;
      })
      .addCase(addSubscriptionData.pending, (state) => {
        state.isFetchingSubscription = true;
      })
      .addCase(addSubscriptionData.rejected, (state, action) => {
        state.createSubscription = {};
        state.isFetchingSubscription = false;
        state.isSuccessCreateSubscription = false;
        state.isErrorCreateSubscription = true;
        state.errorMessageCreateSubscription = action.payload
          ? action.payload.message
          : "Internal error";
      })
      .addCase(editSubscriptionData.fulfilled, (state, action) => {
        state.editSubscription = action.payload;
        state.isFetchingSubscription = false;
        state.isSuccessEditSubscription = true;
        state.isErrorEditSubscription = false;
      })
      .addCase(editSubscriptionData.pending, (state) => {
        state.isFetchingSubscription = true;
      })
      .addCase(editSubscriptionData.rejected, (state, action) => {
        state.editSubscription = {};
        state.isFetchingSubscription = false;
        state.isSuccessEditSubscription = false;
        state.isErrorEditSubscription = true;
        state.errorMessageEditSubscription = action.payload
          ? action.payload.message
          : "Internal error";
      })
      .addCase(deleteSubscriptionData.fulfilled, (state, action) => {
        state.deleteSubscription = action.payload;
        state.isFetchingSubscription = false;
        state.isSuccessDeleteSubscription = true;
        state.isErrorDeleteSubscription = false;
        if (
          state.subscriptionData.data.length === 1 &&
          state.subscriptionData.currentPage ===
            state.subscriptionData.totalPages - 1
        ) {
          state.subscriptionData.currentPage = Math.max(
            0,
            state.subscriptionData.currentPage - 1
          );
        }
        state.subscriptionData.data = state.subscriptionData.data.filter(
          (item) => item.id !== action.meta.arg.id
        );
        state.subscriptionData.totalItems -= 1;
        if (state.subscriptionData.totalItems === 0) {
          state.subscriptionData.totalPages = 0;
        } else if (
          Math.floor(
            (state.subscriptionData.totalItems - 1) /
              state.subscriptionData.pageSize
          ) <
          state.subscriptionData.totalPages - 1
        ) {
          state.subscriptionData.totalPages -= 1;
        }
      })
      .addCase(deleteSubscriptionData.pending, (state) => {
        state.isFetchingSubscription = true;
      })
      .addCase(deleteSubscriptionData.rejected, (state, action) => {
        state.deleteSubscription = {};
        state.isFetchingSubscription = false;
        state.isSuccessDeleteSubscription = false;
        state.isErrorDeleteSubscription = true;
        state.errorMessageDeleteSubscription = action.payload
          ? action.payload.message
          : "Internal error";
      })
      .addCase(loginSubscription.fulfilled, (state, action) => {
        state.subscriptionLoginData = action.payload.data;
        state.isFetchingSubscriptionLogin = false;
        state.isSuccessSubscriptionLogin = true;
        state.isErrorSubscriptionLogin = false;
        state.errorMessageSubscriptionLogin = "";
      
      })
      .addCase(loginSubscription.pending, (state) => {
        state.isFetchingSubscriptionLogin = true;
        state.isSuccessSubscriptionLogin = false;
        state.isErrorSubscriptionLogin = false;
        state.errorMessageSubscriptionLogin = "";
      })
      .addCase(loginSubscription.rejected, (state, action) => {
        state.subscriptionLoginData = {};
        state.isFetchingSubscriptionLogin = false;
        state.isSuccessSubscriptionLogin = false;
        state.isErrorSubscriptionLogin = true;
        state.errorMessageSubscriptionLogin = action.payload
          ? action.payload.message || "Login failed"
          : "Internal error";
      })
      .addCase(submitNumber.fulfilled, (state, action) => {
        state.numberSubmissionData = action.payload.data;
        state.isFetchingNumberSubmission = false;
        state.isSuccessNumberSubmission = true;
        state.isErrorNumberSubmission = false;
        state.errorMessageNumberSubmission = "";
      })
      .addCase(submitNumber.pending, (state) => {
        state.isFetchingNumberSubmission = true;
        state.isSuccessNumberSubmission = false;
        state.isErrorNumberSubmission = false;
        state.errorMessageNumberSubmission = "";
      })
      .addCase(submitNumber.rejected, (state, action) => {
        state.numberSubmissionData = {};
        state.isFetchingNumberSubmission = false;
        state.isSuccessNumberSubmission = false;
        state.isErrorNumberSubmission = true;
        state.errorMessageNumberSubmission = action.payload
          ? action.payload.message || "Number submission failed"
          : "Internal error";
      })
      .addCase(logoutSubscription.fulfilled, (state, action) => {
        state.logoutData = action.payload.data;
        state.isFetchingLogout = false;
        state.isSuccessLogout = true;
        state.isErrorLogout = false;
        state.errorMessageLogout = "";
      })
      .addCase(logoutSubscription.pending, (state) => {
        state.isFetchingLogout = true;
        state.isSuccessLogout = false;
        state.isErrorLogout = false;
        state.errorMessageLogout = "";
      })
      .addCase(logoutSubscription.rejected, (state, action) => {
        state.logoutData = {};
        state.isFetchingLogout = false;
        state.isSuccessLogout = false;
        state.isErrorLogout = true;
        state.errorMessageLogout = action.payload
          ? action.payload.message || "Logout failed"
          : "Internal error";
      })
      .addCase(submitSubscriber.fulfilled, (state, action) => {
        state.subscriberData = action.payload.data;
        state.isFetchingSubscriber = false;
        state.isSuccessSubscriber = true;
        state.isErrorSubscriber = false;
        state.errorMessageSubscriber = "";
      })
      .addCase(submitSubscriber.pending, (state) => {
        state.isFetchingSubscriber = true;
        state.isSuccessSubscriber = false;
        state.isErrorSubscriber = false;
        state.errorMessageSubscriber = "";
      })
      .addCase(submitSubscriber.rejected, (state, action) => {
        state.subscriberData = {};
        state.isFetchingSubscriber = false;
        state.isSuccessSubscriber = false;
        state.isErrorSubscriber = true;
        state.errorMessageSubscriber = action.payload
          ? action.payload.message || "Subscriber creation failed"
          : "Internal error";
      });
  },
});

export const {
  clearStateSubscription,
  clearStateCreateSubscription,
  clearStateDeleteSubscription,
  clearStateEditSubscription,
  clearStateLogin,
  clearStateNumberSubmission,
  clearStateLogout,
  clearStateSubscriber,
} = subscriptionSlice.actions;

export function clearState() {
  return async (dispatch) => {
    dispatch(clearStateSubscription());
  };
}

export function clearStateCreate() {
  return async (dispatch) => {
    dispatch(clearStateCreateSubscription());
  };
}

export function clearStateDelete() {
  return async (dispatch) => {
    dispatch(clearStateDeleteSubscription());
  };
}

export function clearStateEdit() {
  return async (dispatch) => {
    dispatch(clearStateEditSubscription());
  };
}

export function clearLogin() {
  return async (dispatch) => {
    dispatch(clearStateLogin());
  };
}

export function clearNumberSubmission() {
  return async (dispatch) => {
    dispatch(clearStateNumberSubmission());
  };
}

export function clearLogout() {
  return async (dispatch) => {
    dispatch(clearStateLogout());
  };
}

export function clearSubscriber() {
  return async (dispatch) => {
    dispatch(clearStateSubscriber());
  };
}

export const subscriptionSelector = (state) => state.subscription;

export default subscriptionSlice.reducer;

